import React, { createContext, useCallback, useContext, useState } from 'react'

/**
 * Makes screen recording available from anywhere in the app, not just the
 * Documentation "Screen Recording" wizard page.
 *
 * <ScreenRecordingProvider> is mounted once, high up in the app shell
 * (MainApp), alongside <GlobalScreenRecorder/> (the floating "Record your
 * screen" button + its modal). Because MainApp doesn't unmount when the
 * user navigates between routes, the button and the in-progress recording
 * stay alive across page changes.
 *
 * When a recording finishes from that global entry point there's no page
 * to save it to yet — the user could have started it from anywhere. So
 * instead of duplicating the whole Documentation save form in a modal,
 * GlobalScreenRecorder stashes the finished file here as `pendingRecording`
 * and navigates to the existing Screen Recording wizard route, which reads
 * it back out (via useScreenRecordingContext) and skips straight to the
 * "fill in details and save" step, exactly as if it had been recorded
 * on that page directly.
 */
const RecordingContext = createContext(null)

export const ScreenRecordingProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [pendingRecording, setPendingRecordingState] = useState(null)

  const openRecorder = useCallback(() => setIsOpen(true), [])
  const closeRecorder = useCallback(() => setIsOpen(false), [])

  const setPendingRecording = useCallback((file, meta) => {
    setPendingRecordingState({ file, meta })
  }, [])

  const clearPendingRecording = useCallback(() => setPendingRecordingState(null), [])

  const value = {
    isOpen,
    openRecorder,
    closeRecorder,
    pendingRecording,
    setPendingRecording,
    clearPendingRecording,
  }

  return <RecordingContext.Provider value={value}>{children}</RecordingContext.Provider>
}

export const useScreenRecordingContext = () => {
  const ctx = useContext(RecordingContext)
  if (!ctx) {
    throw new Error('useScreenRecordingContext must be used within a <ScreenRecordingProvider>')
  }
  return ctx
}

export default RecordingContext
